import React, { useEffect, useRef, useState } from "react";
import { TextField, InputAdornment } from "@mui/material";
import { Mandatory } from "../../FormHelpers";
import { styled } from "@mui/material/styles";
import { ImagePath } from "../../../../../assets/ImagePath";
import CachedImage from "../../../../common/CachedImage";


const CssTextField = styled(TextField, {
    shouldForwardProp: (prop) => prop !== "helperTextWidth"
})(({ helperTextWidth }) => ({
    "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button": {
        WebkitAppearance: "none",
        margin: 0,
    },
    "& input[type=number]": {
        MozAppearance: "textfield", // Firefox
    },
    "& .MuiFormHelperText-root": {
        color: `#e5453b !important`,
        width: `${helperTextWidth - 30}px`
    },
    "& label": {
        fontFamily: "Roboto",
        fontSize: "14px", // default label font size
    },
    "& label.MuiInputLabel-shrink": {
        fontSize: "14px",
    },
    "& label.Mui-focused": {
        color: "#638dff",
    },
    "& .MuiInput-underline:after": {
        borderBottomColor: "green",
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Roboto",
        "& fieldset": {
            borderColor: "#C4D8FFB2",
        },
        "&:hover fieldset": {
            borderColor: "#92afff",
        },
        "&.Mui-disabled": {
            backgroundColor: "#f4f7fb !important",
            "& fieldset": {
                borderColor: "#c4d8ff",
            },
        },
        "&.Mui-error": {
            "& fieldset": {
                borderColor: "#f2a29d !important",
            },
        },
        "&.Mui-focused fieldset": {
            borderColor: "#C4D8FFB2",
        },

        //Input value color (normal state)
        "& input": {
            color: "#44485A", // Value color
            fontFamily: "Roboto",
        },

        //Placeholder color
        "& input::placeholder": {
            color: "rgba(68, 72, 90, 0.5)", // #80 is 50% opacity in hex
            fontFamily: "Roboto",
            opacity: 1,
        },

        // Disabled input value color
        "& .MuiOutlinedInput-input.Mui-disabled": {
            color: "#44485a",
            WebkitTextFillColor: "#44485a", // Required for proper text color in Chrome
        },

        fontSize: "12px",
        borderRadius: 4,
        backgroundColor: "white",
    },
}));


const TextInput = (props) => {
    const {
        id,
        info,
        readOnly,
        value,
        handleChange,
        mandatory,
        onBlurField,
        placeholder,
        maxLength = 500,
        minLength,
        validation,
        isSections,
        validationResult,
        validationMessage,
        type,
        isSmallScreen,
        title,
        islabelvisible,
        isModal = false,
        height
    } = props;

    const CssTextFieldRef = useRef(null);
    const [inputValue, setInputValue] = useState('');

    const [isFocused, setIsFocused] = useState(false);
    const [helperTextWidth, setHelperTextWidth] = useState(270);

    const isBoolean = (val) => "boolean" === typeof val;
    const isError = validation && isBoolean(validationResult) ? !validationResult : false;
    const helperText = isBoolean(validationResult) && !validationResult ? validationMessage : info;

    useEffect(() => {
        const textFieldWidth = CssTextFieldRef?.current?.offsetWidth
        setHelperTextWidth(textFieldWidth);
    }, []);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    const handleBlur = (e) => {
        handleChange(e, e.target.type);
        onBlurField(e)
        setIsFocused(false)
    };

    const renderType = () => {
        let selectdType = "text";
        switch (type) {
            case "text_field":
                selectdType = "text";
                break;
            case "number":
                selectdType = "number";
                break;
            case "text_area":
                selectdType = "textarea";
                break;
            case "password_field":
                selectdType = "password";
                break;
        }
        return selectdType;
    };

    if (type === "text_area") {
        return islabelvisible ? (
            <div className="relative flex items-start gap-1 update-input">
                <div className="w-full">
                    <div className="flex items-center gap-1 mb-[5px]">
                        {!islabelvisible && <span className="min-w-[17px] min-h-[17px] rounded-7 flex items-center justify-center">
                            <CachedImage
                                image={ImagePath.textAreaField}
                                imgKey="date-picker-Icon-textarea"
                                className="w-[17px] h-[17px]"
                            />
                        </span>}
                        {!islabelvisible && <label className="text-size-14 text-[#15194B] font-regular mt-[2px]">
                            {<><Mandatory mandatory={mandatory} />{title}</>}
                        </label>}
                    </div>
                    <CssTextField
                        id={id}
                        name={id}
                        type={renderType()}
                        rows={height}
                        size="small"
                        fullWidth
                        variant="outlined"
                        disabled={readOnly}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder={isFocused ? "" : placeholder}
                        onFocus={() => setIsFocused(true)}
                        onBlur={handleBlur}
                        inputProps={{
                            maxLength,
                            minLength,
                        }}
                        InputProps={{
                            endAdornment: isError && (
                                <InputAdornment position="end">
                                    <CachedImage
                                        imgKey="warningIcon"
                                        image="/images/warning@2x.png"
                                        width="14px"
                                    />
                                </InputAdornment>
                            ),
                        }}
                        multiline={renderType() === "textarea"}
                        style={{ height: "fit-content" }}
                        helperText={!props.inline && helperText}
                        error={isError}
                    />
                </div>
            </div>
        ) : (
            <div className="relative flex items-start gap-1 update-input">
                <div className="w-full">
                    <div className="flex items-center gap-1 mb-[5px]">
                        {!islabelvisible && <span className="min-w-[17px] min-h-[17px] rounded-7 flex items-center justify-center">
                            <CachedImage
                                image={ImagePath.textAreaField}
                                imgKey="date-picker-Icon-textarea"
                                className="w-[17px] h-[17px]"
                            />
                        </span>}
                        {!islabelvisible && <label className="text-size-14 text-[#15194B] font-regular mt-[2px]">
                            {<>{title}<Mandatory mandatory={mandatory} /></>}
                        </label>}
                    </div>
                    <CssTextField
                        id={id}
                        name={id}
                        rows={height}
                        className="update-input-textarea"
                        type={renderType()}
                        size="small"
                        fullWidth
                        variant="outlined"
                        disabled={readOnly}
                        value={inputValue}
                        InputLabelProps={{
                            shrink: false
                        }}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder={isFocused ? "" : placeholder}
                        onFocus={() => setIsFocused(true)}
                        onBlur={handleBlur}
                        inputProps={{
                            maxLength,
                            minLength,
                        }}
                        InputProps={{
                            endAdornment: isError && (
                                <InputAdornment position="end">
                                    <CachedImage
                                        imgKey="warningIcon"
                                        image="/images/warning@2x.png"
                                        width="14px"
                                    />
                                </InputAdornment>
                            ),
                        }}
                        multiline={renderType() === "textarea"}
                        style={{ height: "fit-content" }}
                        helperText={!props.inline && helperText}
                        error={isError}
                    />
                </div>
            </div>
        );
    }

    return islabelvisible ? (
        <div className="relative flex items-start gap-1 update-input">
            <div className="w-full">
                <div className="flex items-center gap-1 mb-[5px]">
                    {!islabelvisible && <span className="min-w-[17px] min-h-[17px] rounded-7 flex items-center justify-center">
                        <CachedImage
                            image={ImagePath.textField}
                            imgKey="date-picker-Icon2"
                            className="w-[17px] h-[17px]"
                        />
                    </span>}
                    {!islabelvisible && <label className="text-size-14 text-[#15194B] font-regular mt-[2px]">
                        {<>{title}<Mandatory mandatory={mandatory} /></>}
                    </label>}
                </div>
                <CssTextField
                    id={id}
                    name={id}
                    type={renderType()}
                    size="small"
                    fullWidth
                    variant="outlined"
                    disabled={readOnly}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={isFocused ? "" : placeholder}
                    onFocus={() => setIsFocused(true)}
                    onBlur={handleBlur}
                    InputLabelProps={
                        value && {
                            shrink: true,
                        }
                    }
                    inputProps={{
                        maxLength,
                        minLength,
                    }}
                    InputProps={{
                        endAdornment: isError && (
                            <InputAdornment position="end">
                                <CachedImage
                                    imgKey="warningIcon"
                                    image="/images/warning@2x.png"
                                    width="12px"
                                />
                            </InputAdornment>
                        ),
                    }}
                    helperText={!props.inline && helperText}
                    error={isError}
                />
            </div>
        </div>
    ) : (
        <div className="relative flex items-start gap-1 update-input">
            <div className="w-full">
                <div className="flex items-center gap-1 mb-[5px]">
                    <span className="min-w-[17px] min-h-[17px] rounded-7 flex items-center justify-center">
                        <CachedImage
                            image={ImagePath.textField}
                            imgKey="date-picker-Icon2"
                            className="w-[17px] h-[17px]"
                        />
                    </span>
                    {!islabelvisible && <label className="text-size-14 text-[#15194B] font-regular mt-[2px]">
                        {<>{title}<Mandatory mandatory={mandatory} /></>}
                    </label>}
                </div>
                <CssTextField
                    ref={CssTextFieldRef}
                    id={id}
                    name={id}
                    type={renderType()}
                    size="small"
                    fullWidth
                    variant="outlined"
                    disabled={readOnly}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={isFocused ? "" : placeholder}
                    helperTextWidth={helperTextWidth}
                    onFocus={() => setIsFocused(true)}
                    onBlur={handleBlur}
                    sx={{
                        "& .MuiInputBase-root": {
                            backgroundColor: readOnly ? "#F6FBFF" : "#fff",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: readOnly ? "none" : "1px solid #C4D8FFB2",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: readOnly ? "none" : "1px solid #C4D8FFB2",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: readOnly ? "none" : "1px solid #C4D8FFB2",
                        },
                    }}
                    inputProps={{
                        maxLength,
                        minLength,
                    }}
                    InputProps={{
                        endAdornment: isError && (
                            <InputAdornment position="end">
                                <CachedImage
                                    imgKey="warningIcon"
                                    image="/images/warning@2x.png"
                                    width="12px"
                                />
                            </InputAdornment>
                        ),
                    }}
                    helperText={!props.inline && helperText}
                    error={isError}
                />
            </div>
        </div>
    );
};

export default TextInput;